import { ApplicationKeys } from "@/modules/application/common/enum";
import {
  useLazyGetApplicationNumberQuery,
  useSignApplicationMutation
} from "@/modules/application/services";
import { LOGIN_OTP_COUNT } from "@/shared/common/constants";
import { ActionType, AppPaths, CloudflareAsset } from "@/shared/common/enums";
import type { TInsufficientFundsModal } from "@/shared/common/types";
import { BusinessLayout, CloudFlareImage, OTPPopup } from "@/shared/components";
import { useNotify } from "@/shared/hooks";
import { setActiveApplicationId } from "@/shared/state/common";
import { Button } from "@ab.uitools/ui-library/components/Button";
import { Modal } from "@ab.uitools/ui-library/components/Modal";
import { Spinner } from "@ab.uitools/ui-library/components/Spinner";
import type { ISVGIconProps } from "@ab.uitools/ui-library/components/SVGIcons/types";
import { useFormProps } from "@ab.uitools/ui-library/hooks/useFormProps";
import { type ReactElement, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

interface Props<T> {
  title?: string,
  children: ReactElement,
  getApplicationId: (values: T) => Promise<string>,
  page: string;
  isEdit?: boolean;
  isLoading: boolean;
  isSaveDisabled?: boolean;
  isSignDisabled?: boolean;
  isGetApplicationLoading?: boolean;
  hasChanges?: boolean;
  className?: string;
}

export const ApplicationLayout = <_, T>(
  {
    children,
    title,
    isLoading,
    isSaveDisabled,
    isSignDisabled,
    isGetApplicationLoading,
    getApplicationId,
    page,
    isEdit = false,
    hasChanges = false,
    className,
  }: Props<T>,
) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const {
    showErrorAlertNotification,
    showWarningAlertNotification,
    showSuccessSnackbarNotification,
  } = useNotify();
  const { setValue, setError, watch, trigger, isSubmitting } = useFormProps();
  const dispatch = useDispatch();

  const [isSaved, setIsSaved] = useState<boolean>(false);
  const [isOtpModalOpen, setIsOtpModalOpen] = useState<boolean>(false);
  const [isModalOpen, setToggleModal] = useState<boolean>(false);
  const [savedApplicationId, setSavedApplicationId] = useState<string | null>(null);

  const [signApplication] = useSignApplicationMutation();
  const [getApplicationNumber] = useLazyGetApplicationNumberQuery();

  const values = watch?.() || {};

  const handleSaveAndGetApplicationId = async (isSave?: boolean): Promise<string> => {
    return getApplicationId(values as T)
      .then((id) => (id))
      .catch((error) => {
        const errors = error?.data?.errors;
        const warning = error?.data?.warnings?.[0];

        if (warning) {
          showWarningAlertNotification(t(warning));
          if (isSave) {
            setToggleModal(false);
            document.getElementById("form-container")?.scrollTo({ top: 0, behavior: "smooth" });
          }
        } else if (errors) {
          Object.keys(errors).forEach((key) => {
            setError?.(key, { message: errors[key] });
            setToggleModal(false);
          });
        } else {
          showErrorAlertNotification(error);
          document.getElementById("form-container")?.scrollTo({ top: 0, behavior: "smooth" });
        }
        return Promise.reject(error);
      }).finally(() => {
        dispatch(setActiveApplicationId(null));
      });
  };

  const handleGetApplicationId = async () => {
    if (savedApplicationId) {
      return savedApplicationId;
    } else {
      const id = await handleSaveAndGetApplicationId();
      setSavedApplicationId(id);
      setIsSaved(true);
      return id;
    }
  };

  const handleChangeToken = async (token: string) => {
    if (token.length === LOGIN_OTP_COUNT) {
      handleGetApplicationId().then((id) => {
        setIsSaved(true);
        signApplication({
          id,
          otp: token,
        }).unwrap().then(() => {
          showSuccessSnackbarNotification(t("alerts.applicationHasBeenSuccessfullySigned"));
          setTimeout(() => navigate(AppPaths.ApplicationHistory));
        }).catch((error) => {
          showErrorAlertNotification(error);
        });
      });
    }
  };

  const handleSaveApplication = () => {
    handleSaveAndGetApplicationId(true).then(() => {
      setIsSaved(true);
      showSuccessSnackbarNotification(t("alerts.applicationHasBeenSuccessfullySaved"));
      setTimeout(() => navigate(AppPaths.ApplicationHistory));
    }).finally(() => {
      setToggleModal(false);
    });
  };

  const handleCheckFunds = (actionType: TInsufficientFundsModal) => {
    trigger?.().then((isValid) => {
      if (isValid) {
        if (actionType === ActionType.Sign) {
          setIsOtpModalOpen(true);
        } else {
          setToggleModal(true);
        }
      }
    });
  };

  const cancelApplicationEdit = () => {
    navigate(-1);
  };

  useEffect(() => {
    if (!isEdit) {
      getApplicationNumber()
        .unwrap()
        .then((applicationNumber) => {
          setValue(ApplicationKeys.ApplicationNumber, applicationNumber);
        });
    }
  }, []);

  return (
    <BusinessLayout
      title={title}
      isEdit={isEdit}
      isSaved={isSaved}
      className={className}
      hasChanges={hasChanges}
    >
      <>
        <OTPPopup
          togglePopup={setIsOtpModalOpen}
          isOpen={isOtpModalOpen}
          isLoading={isLoading}
          handleChangeToken={handleChangeToken}
        />
        <Modal
          isOpen={isModalOpen}
          onClose={() => setToggleModal(false)}
          onSubmit={handleSaveApplication}
          titleProps={{
            title: t("application.saveModal.title"),
            size: "large"
          }}
          subtitle={t("application.saveModal.description")}
          closeIcon={!isLoading}
          closeOnOutsideClick={!isLoading}
          buttonProps={{
            cancel: {
              type: "tertiary",
              buttonText: t("general.cancel"),
              buttonActionType: "button",
              onClick: () => setToggleModal(false),
              disabled: isLoading,
            },
            confirm: {
              type: "primary",
              buttonText: t("general.save"),
              buttonActionType: "submit",
              isLoading: isLoading,
              disabled: isLoading,
            },
          }}
        />
        {isGetApplicationLoading ? (
          <div className="flexbox align-items--center" style={{ minHeight: "50vh" }}>
            <Spinner isFullScreen={false}/>
          </div>
        ) : children}
        {isEdit ? (
          <div
            className="full-width flexbox flex-direction--row justify-content--end form-actions">
            <Button
              className="mt-32"
              buttonActionType="submit"
              disabled={isSubmitting}
              type="secondary"
              onClick={cancelApplicationEdit}
              isLoading={isSubmitting}
              buttonText={t("general.cancel")}
              data-testid="cancel-application-edit"
              data-ga-id="cancel-application-edit"
              data-ga-action={`Cancel ${page} application edit`}
            />
            <Button
              className="mt-32 ml-16 submit-button"
              buttonActionType="submit"
              buttonText={t("general.saveChanges")}
              data-testid="save-application-change"
              data-ga-id="save-application-changes"
              data-ga-action={`Save ${page} application changes`}
              disabled={isSubmitting || !hasChanges || isSaveDisabled}
              onClick={() => handleCheckFunds(ActionType.Save)}
            />
          </div>
        ) : (
          <div
            className="full-width flexbox flex-direction--row justify-content--end form-actions">
            <Button
              className="mt-32"
              buttonActionType="submit"
              buttonText={t("general.save")}
              type="secondary"
              data-testid="save-application"
              data-ga-id="save-application"
              data-ga-action={`Save ${page} application`}
              disabled={isSubmitting || isSaveDisabled}
              onClick={() => handleCheckFunds(ActionType.Save)}
              iconProps={{
                Component: (props: ISVGIconProps) => (
                  <CloudFlareImage {...props} path={CloudflareAsset.Save}/>
                ),
                alignment: "left",
              }}
            />
            <Button
              className="mt-32 ml-16 submit-button"
              buttonActionType="submit"
              disabled={isSubmitting || isSignDisabled}
              onClick={() => handleCheckFunds(ActionType.Sign)}
              isLoading={isSubmitting}
              buttonText={t("general.sign")}
              data-testid="sign-application"
              data-ga-id="sign-application"
              data-ga-action={`Sign ${page} application`}
              iconProps={{
                Component: (props: ISVGIconProps) => (
                  <CloudFlareImage
                    {...props}
                    path={CloudflareAsset.SignLight}
                  />
                ),
                alignment: "left",
                type: "primary",
              }}
            />
          </div>
        )}
      </>
    </BusinessLayout>
  );
};
